import { useEffect, useRef, useState } from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import {
  mergePdfs,
  pdfPageCount,
  renderPdfPageThumb,
} from "../lib/pdfOps";
import { saveAndShareBytes } from "../lib/shareText";
import { cloneArrayBuffer } from "../lib/file";
import { ToolPageShell } from "./ToolPages";

type MergeProps = {
  currentBuffer?: ArrayBuffer | null;
  currentName?: string;
  onClose: () => void;
  onMinimize?: () => void;
};

type MergeItem = {
  id: string;
  name: string;
  buffer: ArrayBuffer;
  thumb?: string | null;
  pages?: number;
};

export function PdfMergeSheet({
  currentBuffer,
  currentName,
  onClose,
  onMinimize,
}: MergeProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<MergeItem[]>(() =>
    currentBuffer
      ? [
          {
            id: crypto.randomUUID(),
            name: currentName || "فعلی.pdf",
            buffer: currentBuffer,
          },
        ]
      : []
  );
  const [name, setName] = useState("nevisign-merge-1.pdf");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const dragId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const pending = items.filter((it) => it.thumb === undefined);
    if (!pending.length) return;
    void (async () => {
      for (const it of pending) {
        const thumb = await renderPdfPageThumb(it.buffer, 1, 88);
        const pages = await pdfPageCount(it.buffer).catch(() => 0);
        if (cancelled) return;
        setItems((prev) =>
          prev.map((p) =>
            p.id === it.id
              ? { ...p, thumb: thumb?.dataUrl ?? null, pages }
              : p
          )
        );
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only re-run when item ids change (new files added)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => i.id).join("|")]);

  const onPick = async (list: FileList | null) => {
    if (!list?.length) return;
    const next: MergeItem[] = [];
    for (const f of Array.from(list)) {
      const buf = cloneArrayBuffer(await f.arrayBuffer());
      next.push({
        id: crypto.randomUUID(),
        name: f.name,
        buffer: buf,
      });
    }
    setItems((prev) => [...prev, ...next]);
  };

  const reorder = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    setItems((prev) => {
      const from = prev.findIndex((i) => i.id === fromId);
      const to = prev.findIndex((i) => i.id === toId);
      if (from < 0 || to < 0) return prev;
      const copy = [...prev];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved!);
      return copy;
    });
  };

  const run = async () => {
    if (items.length < 1) {
      setHint("حداقل یک فایل اضافه کنید.");
      return;
    }
    setBusy(true);
    setHint(null);
    try {
      const buffers = items.map((i) => i.buffer);
      const order = items.map((_, i) => i);
      const bytes = await mergePdfs(buffers, order);
      const fileName = name.endsWith(".pdf") ? name : `${name}.pdf`;
      await saveAndShareBytes(bytes, fileName, "application/pdf");
      setHint("فایل ادغام‌شده آماده اشتراک شد.");
    } catch (err) {
      setHint(err instanceof Error ? err.message : "خطا در ادغام");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ToolPageShell
      title="ادغام اسناد PDF"
      toolId="pdf-merge"
      onClose={onClose}
      onMinimize={onMinimize}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => inputRef.current?.click()}
          >
            <Plus size={16} /> افزودن PDF
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={busy || items.length < 1}
            onClick={() => void run()}
          >
            {busy ? "…" : "ادغام و اشتراک"}
          </button>
        </>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        className="sr-only"
        onChange={(e) => {
          void onPick(e.target.files);
          e.target.value = "";
        }}
      />
      <ul className="merge-list">
        {items.map((it) => (
          <li
            key={it.id}
            className="merge-item"
            draggable
            onDragStart={() => {
              dragId.current = it.id;
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              if (dragId.current) reorder(dragId.current, it.id);
              dragId.current = null;
            }}
            onTouchStart={() => {
              dragId.current = it.id;
            }}
            onTouchMove={(e) => {
              const t = e.touches[0];
              if (!t || !dragId.current) return;
              const el = document.elementFromPoint(t.clientX, t.clientY);
              const row = el?.closest(".merge-item") as HTMLElement | null;
              const targetId = row?.dataset.id;
              if (targetId && targetId !== dragId.current) {
                reorder(dragId.current, targetId);
                dragId.current = targetId;
              }
            }}
            data-id={it.id}
          >
            <span className="merge-grip" aria-hidden>
              <GripVertical size={16} />
            </span>
            <div className="merge-thumb">
              {it.thumb ? (
                <img src={it.thumb} alt="" />
              ) : (
                <span className="merge-thumb-ph">PDF</span>
              )}
            </div>
            <div className="merge-meta">
              <strong>{it.name}</strong>
              {it.pages != null && it.pages > 0 && (
                <span>{it.pages.toLocaleString("fa-IR")} صفحه</span>
              )}
            </div>
            <button
              type="button"
              className="icon-btn compact"
              aria-label="حذف"
              onClick={() =>
                setItems((prev) => prev.filter((p) => p.id !== it.id))
              }
            >
              <Trash2 size={14} />
            </button>
          </li>
        ))}
      </ul>
      <label className="settings-hint" style={{ display: "block", marginTop: "0.85rem" }}>
        نام خروجی
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ display: "block", width: "100%", marginTop: "0.35rem" }}
        />
      </label>
      {hint && <p className="settings-hint">{hint}</p>}
    </ToolPageShell>
  );
}
